/**
 * Canonical registry of every toggleable feature in the app.
 *
 * The main admin can hide any of these from the Public landing or the
 * logged-in Member experience via Admin → Feature visibility. Each entry
 * declares which audiences it can be toggled for; the admin editor uses
 * this to render only the relevant switches.
 *
 * Adding a new toggleable feature = adding one row here + wrapping the
 * page/tile with `useFeatureEnabled(key, audience)` on the consuming
 * component.
 */
import { useAuth } from "@/lib/auth";

// audiences: "public" (unauth landing/PublicShell), "member" (logged-in app)
export const FEATURE_REGISTRY = [
  { key: "announcements",  labelKey: "announcements",  audiences: ["public", "member"] },
  { key: "events",         labelKey: "events",         audiences: ["public", "member"] },
  { key: "sermons",        labelKey: "sermons",        audiences: ["public", "member"] },
  { key: "yearbook",       labelKey: "yearbook",       audiences: ["member"] },
  { key: "members",        labelKey: "members",        audiences: ["member"] },
  { key: "messages",       labelKey: "messages",       audiences: ["member"] },
  { key: "board",          labelKey: "board",          audiences: ["public", "member"] },
  { key: "pastor",         labelKey: "pastor",         audiences: ["public", "member"] },
  { key: "firstElder",     labelKey: "firstElder",     audiences: ["public", "member"] },
  { key: "departments",    labelKey: "departments",    audiences: ["public", "member"] },
  { key: "live",           labelKey: "live",           audiences: ["public", "member"] },
  { key: "prayer",         labelKey: "prayers",        audiences: ["public", "member"] },
  { key: "sabbathLessons", labelKey: "sabbathLessons", audiences: ["public", "member"] },
  { key: "bible",          labelKey: "bible",          audiences: ["public", "member"] },
  { key: "songbook",       labelKey: "songbook",       audiences: ["public", "member"] },
  { key: "giving",         labelKey: "giving",         audiences: ["public", "member"] },
  { key: "carpool",        labelKey: "carpool",        audiences: ["member"] },
  { key: "about",          labelKey: "aboutUs",        audiences: ["public", "member"] },
  { key: "visit",          labelKey: "visitInPerson",  audiences: ["public", "member"] },
  { key: "gallery",        labelKey: "gallery",        audiences: ["public", "member"] },
  { key: "voice",              labelKey: "voiceCalls",        audiences: ["member"] },
  { key: "voice_direct",       labelKey: "voiceDirectFlag",   audiences: ["member"] },
  { key: "voice_department",   labelKey: "voiceDeptFlag",     audiences: ["member"] },
  { key: "sabbathSet",         labelKey: "sabbathSet",        audiences: ["member"] },
  { key: "birthdays",          labelKey: "birthdays",         audiences: ["member"] },
  // Toggle for the auto-push that fires the moment the church's
  // YouTube live broadcast goes on-air. Member-audience only — no
  // "public" column since visitors can't receive pushes anyway.
  { key: "live_push",          labelKey: "liveStartPush",     audiences: ["member"] },
];

export const FEATURE_KEYS = FEATURE_REGISTRY.map((f) => f.key);

/**
 * Resolve whether a feature is enabled for a given audience. Defaults to
 * ON when a church has never configured its flags (so upgrading in-place
 * never silently hides features from existing tenants).
 *
 * `platformDisabled` (optional) is a list of feature keys the super
 * admin has killed globally — its presence forces isEnabled=false
 * regardless of the per-tenant flags. Backend enforces the same via
 * middleware, so any UI that skips this check just shows a tile that
 * 503s on click.
 */
export function isEnabled(flags, key, audience, platformDisabled) {
  if (Array.isArray(platformDisabled) && platformDisabled.includes(key)) return false;
  if (!flags) return true;
  const f = flags[key];
  if (!f) return true;
  const v = f[audience];
  return v === undefined || v === null ? true : Boolean(v);
}

/** Hook used across member-authenticated pages. Reads from the church
 *  document held in the auth context. */
export function useFeatureEnabled(key, audience = "member") {
  const { church } = useAuth();
  return isEnabled(church?.feature_flags, key, audience, church?._platform_disabled);
}

/** Filter a list of tile-like items (`{ key, ... }`) down to enabled ones. */
export function filterEnabled(items, flags, audience, platformDisabled) {
  return (items || []).filter((it) => isEnabled(flags, it.key, audience, platformDisabled));
}

/**
 * Reorder a tile list to match the admin-defined ordering while
 * preserving any tiles the admin hasn't sorted yet (new features added
 * in future versions should still show up).
 *
 * `order` is an array of tile-keys from the church's `tile_orders`
 * field. Any listed key is placed first in the given order; unlisted
 * keys keep their natural order and are appended at the end.
 */
export function applyTileOrder(items, order) {
  if (!order || !Array.isArray(order) || order.length === 0) return items;
  const byKey = new Map(items.map((it) => [it.key, it]));
  const seen = new Set();
  const sorted = [];
  for (const key of order) {
    const item = byKey.get(key);
    if (item) {
      sorted.push(item);
      seen.add(key);
    }
  }
  for (const item of items) {
    if (!seen.has(item.key)) sorted.push(item);
  }
  return sorted;
}
